import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/router";
import { useState, useEffect, useRef } from "react";
import { ChartArea, Menu, X } from "lucide-react";
import Button from "../ui/Button";
import { useModal } from "../../context/ModalContext";

export default function Header() {
  const router = useRouter();
  const { openModal } = useModal();
  const [sideBarOpen, setSideBarOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [techMenuOpen, setTechMenuOpen] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [servicesMenuOpen, setServicesMenuOpen] = useState(false);
  const [headerData, setHeaderData] = useState(null);
  const techMenuRef = useRef(null);
  const productsMenuRef = useRef(null);
  const servicesMenuRef = useRef(null);

  const categoryToTechMap = {};

  headerData?.services?.forEach((service) => {
    const subTechnologies = service.sub_technologies || [];

    const technologyTitles = subTechnologies
      .map((sub) => {
        const tech = headerData.technologies?.find(
          (t) => t.id === sub.technology_id
        );
        return tech?.title?.toLowerCase();
      })
      .filter(Boolean);

    categoryToTechMap[service.name] = [...new Set(technologyTitles)];
  });

  useEffect(() => {
    async function getData() {
      const BACKEND_URL = process.env.NEXT_PUBLIC_BACKEND_URL;
      try {
        const formData = new FormData();
        formData.append("action", "getHeaderData");

        const response = await fetch(`${BACKEND_URL}/manageData`, {
          method: "POST",
          body: formData,
        });
        const data = await response.json();
        setHeaderData(data);
      } catch (error) {
        console.log("error", error);
      }
    }
    getData();
  }, []);

  const serviceCategories = headerData?.services || [];

  const [hoveredCategory, setHoveredCategory] = useState(
    serviceCategories[0]?.name
  );

  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 10;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    if (serviceCategories.length > 0) {
      setHoveredCategory(serviceCategories[0]?.name)
    }

    const handleClickOutside = (event) => {
      if (techMenuRef.current && !techMenuRef.current.contains(event.target)) {
        setTechMenuOpen(false);
      }
      if (
        productsMenuRef.current &&
        !productsMenuRef.current.contains(event.target)
      ) {
        setProductsMenuOpen(false);
      }
      if (
        servicesMenuRef.current &&
        !servicesMenuRef.current.contains(event.target)
      ) {
        setServicesMenuOpen(false);
      }
    };

    const handleRouteChange = (e) => {
      setTechMenuOpen(false);
      setProductsMenuOpen(false);
      setServicesMenuOpen(false);
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);
    router.events.on("routeChangeStart", handleRouteChange);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
      router.events.off("routeChangeStart", handleRouteChange);
    };
  }, [scrolled, techMenuRef, productsMenuRef, servicesMenuRef, router.events, serviceCategories]);

  useEffect(() => {
    const handleHashChange = () => {
      setTechMenuOpen(false);
      setProductsMenuOpen(false);
      setServicesMenuOpen(false);
    };

    window.addEventListener("hashchange", handleHashChange);

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const navItems = [
    { name: "Home", path: "/" },
    {
      name: "Services",
      path: "/services",
      hasSubmenu: true,
    },
    { name: "Culture", path: "/culture" },
    { name: "Careers", path: "/careers" },
    { name: "Portfolio", path: "/portfolio" },
    { name: "About Us", path: "/about-us" },
    { name: "Contact Us", path: "/contact-us" },
  ];

  useEffect(() => {
    ['body', 'html'].forEach((el) =>
      document.querySelector(el)?.classList.toggle('overflow-hidden', sideBarOpen)
    );
  }, [sideBarOpen]);

  return (
    <header
      className={`fixed top-0 left-0 z-[999] right-0 ${scrolled ? "bg-white shadow-md z-50 " : "bg-transparent  z-50"
        } transition-all duration-300  py-2`}
    >
      <div className="px-6 mx-auto w-full relative flex items-center justify-between">
        <Link
          href={"/"}
          className="relative w-[70px] h-[70px]"
          aria-label="Go to homepage"
        >
          <Image
            src="/images/logo-white.webp"
            alt="SCC Infotech7"
            width={200}
            height={60}
            loading="eager"
            sizes="(max-width: 640px) 60px, (max-width: 1024px) 70px, 90px"
            className="object-contain"
          />
        </Link>

        <nav className="hidden  lg:flex gap-8 items-center">
          {navItems.map((item, index) => (
            <div
              key={index}
              className=""
              ref={
                item.name === "Technologies"
                  ? techMenuRef
                  : item.name === "Products"
                    ? productsMenuRef
                    : item.name === "Services"
                      ? servicesMenuRef
                      : null
              }
              onMouseEnter={() => {
                if (item.name === "Technologies") {
                  setTechMenuOpen(true);
                  setProductsMenuOpen(false);
                  setServicesMenuOpen(false);
                } else if (item.name === "Services") {
                  setServicesMenuOpen(true);
                  setTechMenuOpen(false);
                  setProductsMenuOpen(false);
                }
              }}
              onMouseLeave={() => {
                if (item.name === "Technologies") {
                  setTechMenuOpen(false);
                } else if (item.name === "Services") {
                  setServicesMenuOpen(false);
                }
              }}
            >
              <Link
                href={
                  item.path == "services"
                    ? `${item.path}?tab=services`
                    : item.path
                }
                aria-label={`Navigate to ${item.name} page`}
                className={`px-2 py-1 text-lg font-medium relative group ${router.pathname === "/"
                  ? scrolled
                    ? router.pathname === item.path ||
                      router.pathname.startsWith(`${item.path}/`)
                      ? "text-[#172C5E]"
                      : "text-gray-800 hover:text-[#172C5E]"
                    : router.pathname === item.path ||
                      router.pathname.startsWith(`${item.path}/`)
                      ? "text-white"
                      : "text-white hover:text-white"
                  : router.pathname === item.path ||
                    router.pathname.startsWith(`${item.path}/`)
                    ? "text-[#172C5E]"
                    : "text-gray-800 hover:text-[#172C5E]"
                  } transition-colors duration-300 flex items-center`}
              >
                {item.name}
                {item.hasSubmenu && (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className={`h-4 w-4 ml-1 transition-transform duration-300 ${(item.name === "Technologies" && techMenuOpen) ||
                      (item.name === "Products" && productsMenuOpen) ||
                      (item.name === "Services" && servicesMenuOpen)
                      ? "rotate-180"
                      : ""
                      }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                )}
                <span
                  className={`absolute bottom-0 left-0 w-0 h-0.5 ${router.pathname === "/" && !scrolled
                    ? "bg-[#7938DC]"
                    : "bg-[#172C5E]"
                    } group-hover:w-full transition-all duration-300 ${router.pathname === item.path ||
                      router.pathname.startsWith(`${item.path}/`)
                      ? "w-full"
                      : "w-0"
                    }`}
                />
              </Link>
              {item.name === "Services" && item.hasSubmenu && (
                <div
                  className={`absolute top-full left-1/2 -translate-x-1/2 transform translate-y-2 w-[1200px] max-w-[98vw] bg-white/95 backdrop-blur-lg shadow-2xl rounded-2xl overflow-hidden transition-all duration-300 border border-[#e5e7eb] ${servicesMenuOpen
                    ? "opacity-100 visible"
                    : "opacity-0 invisible"
                    }`}
                  style={{ zIndex: 1000 }}
                >
                  <div
                    className="flex"
                    style={{ minHeight: "400px", maxHeight: "540px" }}
                  >
                    <div className="w-[25%] border-r border-gray-200 bg-white/98 p-6 overflow-y-auto">
                      <h4 className="text-lg font-bold text-[#172C5E] mb-4">
                        Service Categories
                      </h4>
                      <div className="space-y-2">
                        {serviceCategories.map((category) => (
                          <div
                            key={category.name}
                            onMouseEnter={() =>
                              setHoveredCategory(category.name)
                            }
                            onClick={() => {
                              if (category.name === "ATS Score Checker") {
                                router.push("/ats-score-checker");
                              } else {
                                router.push(`/services?tab=services#services`);
                              }
                              setServicesMenuOpen(false);
                            }}
                            className={`px-3 py-2 rounded cursor-pointer font-medium transition-all duration-200 flex items-center gap-2 ${hoveredCategory === category.name
                              ? "bg-[#8997FB]/20 text-[#8339EA]"
                              : "hover:bg-[#8997FB]/10 text-gray-700"
                              }`}
                          >
                            <span
                              className="w-7 h-7 flex items-center justify-center rounded-md bg-[#8997FB]/10 text-lg"
                              style={{ color: category.color }}
                            >
                              <span
                                dangerouslySetInnerHTML={{
                                  __html: category.icon,
                                }}
                              ></span>
                            </span>
                            {category.name}
                          </div>
                        ))}
                        <div
                          onMouseEnter={() =>
                            setHoveredCategory("ATS Score Checker")
                          }
                          onClick={() => {
                            router.push("/ats-score-checker");
                            setServicesMenuOpen(false);
                          }}
                          className={`px-3 py-2 rounded cursor-pointer font-medium transition-all duration-200 flex items-center gap-2 ${hoveredCategory === "ATS Score Checker"
                            ? "bg-[#8997FB]/20 text-[#8339EA]"
                            : "hover:bg-[#8997FB]/10 text-gray-700"
                            }`}
                        >
                          <span
                            className="w-7 h-7 flex items-center justify-center rounded-md bg-[#8997FB]/10 text-lg"
                            style={{ color: "#8339EA" }}
                          >
                            <span>
                              <ChartArea />
                            </span>
                          </span>
                          ATS Score Checker
                        </div>
                      </div>
                    </div>

                    <div className="w-[75%] p-6 bg-white/90 flex flex-col">
                      <h4 className="text-lg font-bold text-[#172C5E] mb-4">
                        Technologies
                      </h4>
                      {hoveredCategory &&
                        categoryToTechMap[hoveredCategory] &&
                        categoryToTechMap[hoveredCategory].length > 0 ? (
                        (() => {
                          const techIds = categoryToTechMap[hoveredCategory];
                          const techCards = techIds.map((techId) =>
                            headerData?.technologies.find(
                              (t) =>
                                t?.title?.toLowerCase() ===
                                techId?.toLowerCase()
                            )
                          );

                          let rows = [];
                          if (techCards.length === 1) {
                            rows = [techCards];
                          } else if (techCards.length === 2) {
                            rows = [techCards];
                          } else if (techCards.length === 3) {
                            rows = [techCards.slice(0, 2), techCards.slice(2)];
                          } else if (techCards.length === 4) {
                            rows = [techCards.slice(0, 2), techCards.slice(2)];
                          } else if (techCards.length === 5) {
                            rows = [techCards.slice(0, 3), techCards.slice(3)];
                          } else if (techCards.length === 6) {
                            rows = [
                              techCards.slice(0, 3),
                              techCards.slice(3, 5),
                              techCards.slice(5),
                            ];
                          } else if (techCards.length === 7) {
                            rows = [
                              techCards.slice(0, 3),
                              techCards.slice(3, 5),
                              techCards.slice(5),
                            ];
                          } else if (techCards.length > 7) {
                            let i = 0;
                            rows = [techCards.slice(0, 3)];
                            i = 3;
                            while (i < techCards.length) {
                              rows.push(techCards.slice(i, i + 2));
                              i += 2;
                            }
                          }
                          return (
                            <div className="flex flex-col gap-4 w-full">
                              {rows.map((row, rowIdx) => (
                                <div
                                  key={rowIdx}
                                  className="flex w-full gap-4 justify-center"
                                >
                                  {row.map((techCat, idx) => {
                                    const techs = techCat?.sub_technologies;
                                    let row1 = [],
                                      row2 = [];
                                    if (techs?.length === 3) {
                                      row1 = techs.slice(0, 2);
                                      row2 = techs.slice(2);
                                    } else if (techs?.length <= 3) {
                                      row1 = techs;
                                    } else if (techs?.length === 4) {
                                      row1 = techs.slice(0, 2);
                                      row2 = techs.slice(2);
                                    } else if (techs?.length === 5) {
                                      row1 = techs.slice(0, 3);
                                      row2 = techs.slice(3);
                                    } else if (techs?.length === 6) {
                                      row1 = techs.slice(0, 3);
                                      row2 = techs.slice(3);
                                    } else if (techs?.length === 7) {
                                      row1 = techs.slice(0, 4);
                                      row2 = techs.slice(4);
                                    } else if (techs?.length > 7) {
                                      row1 = techs.slice(
                                        0,
                                        Math.ceil(techs.length / 2)
                                      );
                                      row2 = techs.slice(
                                        Math.ceil(techs.length / 2)
                                      );
                                    }

                                    let cardClass = "flex-1";
                                    if (row.length === 1) cardClass = "w-full";
                                    else if (row.length === 2)
                                      cardClass = "w-1/2";
                                    else if (row.length === 3)
                                      cardClass = "w-1/3";
                                    else if (row.length === 4)
                                      cardClass = "w-1/4";
                                    return (
                                      <div
                                        key={techCat?.id}
                                        className={`${cardClass} rounded-xl bg-white/90 shadow-sm hover:shadow-md border border-slate-100 hover:border-[#8997FB]/50 group transition-all duration-200 p-4 flex flex-col items-center text-center cursor-pointer`}
                                        onClick={() => {
                                          router.push(
                                            `/services?tab=technologies#technologies`
                                          );
                                          setServicesMenuOpen(false);
                                        }}
                                      >
                                        <span
                                          className={`w-10 h-10 flex items-center justify-center rounded-full mb-2 shadow-sm border-[#8339EA] bg-[#8339EA1A] [&>svg]:text-[#8339EA] border `}
                                          dangerouslySetInnerHTML={{
                                            __html: techCat?.icon,
                                          }}
                                        ></span>
                                        <span
                                          className="font-bold text-[#8339EA] text-base mb-1"
                                          style={{ color: techCat?.color }}
                                        >
                                          {techCat?.title}
                                        </span>
                                        <div className="flex flex-col w-full gap-1 justify-center mt-1">
                                          {row1.length > 0 && (
                                            <div className="flex w-full gap-2 justify-center">
                                              {row1.map((tech) => {
                                                return (
                                                  <span
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      router.push(
                                                        `/technologies/details/${tech?.title
                                                          .toLowerCase()
                                                          .replace(
                                                            /[\s.]+/g,
                                                            "-"
                                                          )}`
                                                      );
                                                    }}
                                                    key={tech?.id}
                                                    className="flex-1 text-[11px] bg-[#8339EA1A] rounded px-2 py-1 text-gray-700 hover:text-[#8339EA] transition-colors duration-200 text-center font-medium"
                                                  >
                                                    {tech?.title}
                                                  </span>
                                                )
                                              })}
                                            </div>
                                          )}
                                          {row2.length > 0 && (
                                            <div className="flex w-full gap-2 justify-center">
                                              {row2.map((tech) => (
                                                <span
                                                  onClick={(e) => {
                                                    e.stopPropagation();
                                                    router.push(
                                                      `/technologies/details/${tech?.title
                                                        .toLowerCase()
                                                        .replace(
                                                          /[\s.]+/g,
                                                          "-"
                                                        )}`
                                                    );
                                                  }}
                                                  key={tech?.id}
                                                  className="flex-1 text-[11px] bg-[#8339EA1A] rounded px-2 py-1 text-gray-700 hover:text-[#8339EA] transition-colors duration-200 text-center font-medium"
                                                >
                                                  {tech?.title}
                                                </span>
                                              ))}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ))}
                            </div>
                          );
                        })()
                      ) : (
                        <div className="text-gray-400 text-sm flex-1 flex items-center justify-center">
                          Hover a service category to see technologies
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Button
            onClick={openModal}
            variant="primary"
            size="md"
            rightIcon={<span>→</span>}
          >
            Get a Quote
          </Button>
        </div>

        <div
          className={`lg:hidden relative z-[9999] ${scrolled ? "bg-white" : "bg-[#172C5E]/20"
            } rounded-full p-2.5 hover:shadow-md transition-all duration-300`}
          onClick={() => {
            const updated = !sideBarOpen;
            setSideBarOpen(updated);

          }}

        >
          {sideBarOpen ? (
            <X
              className={`text-xl ${scrolled ? "text-[#172C5E]" : "text-white"
                }`}
            />
          ) : (
            <Menu
              className={`text-xl ${scrolled ? "text-[#172C5E]" : "text-white"
                }`}
            />
          )}
        </div>

        <>
          <div
            className={`fixed inset-0  z-[9998] transition-opacity duration-300 ${sideBarOpen ? "bg-black/10" : "bg-opacity-0 pointer-events-none"
              }`}
            onClick={() => setSideBarOpen(false)}
          />
          <div
            className={`fixed top-0 right-0 h-full w-80 max-w-full bg-gradient-to-b from-white to-gray-50 shadow-2xl z-[9999] transition-transform duration-300 transform ${sideBarOpen ? "translate-x-0" : "translate-x-full"
              }`}
            style={{ minWidth: 320 }}
          >
            <div className="py-6 px-4 h-full flex flex-col">
              <div className="flex justify-between items-center mb-8">
                <Link
                  href="/"
                  aria-label="Return to homepage"
                  className="relative w-[120px] h-[60px] block"
                >
                  <Image
                    src="/images/logo.webp"
                    alt="SCC Infotech3"
                    fill
                    priority
                    loading="eager"
                    sizes="(max-width: 768px) 90px, 120px"
                    className="object-contain"
                  />
                </Link>
                <button
                  className="ml-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                  onClick={() => setSideBarOpen(false)}
                  aria-label="Close sidebar"
                >
                  <X className="text-2xl text-[#172C5E]" />
                </button>
              </div>
              <nav className="flex flex-col gap-4 flex-1 overflow-y-auto">
                {navItems.map((item, index) => (
                  <div key={index}>
                    <div
                      className={`w-full px-3 py-2.5 rounded-md ${router.pathname === item.path ||
                        router.pathname.startsWith(`${item.path}/`)
                        ? "bg-[#2c559b]/10 text-[#2c559b] font-medium"
                        : "text-gray-700 hover:bg-gray-100"
                        } transition-colors min-w-[210px] duration-200 flex justify-between items-center cursor-pointer`}
                      onClick={() => {
                        const safePath = item.path === "//" ? "/" : item.path;
                        router.push(safePath);
                        setSideBarOpen(false);
                      }}
                    >
                      <span>{item.name}</span>
                    </div>
                  </div>
                ))}

                <Button
                  onClick={() => {
                    setSideBarOpen(false);
                    openModal();
                  }}
                  variant="primary"
                  size="md"
                  fullWidth
                  rightIcon={<span>→</span>}
                  className="mt-4"
                >
                  Get a Quote
                </Button>
              </nav>
            </div>
          </div>
        </>
      </div>
    </header>
  );
}
